import {
  Autocomplete,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  List,
  TextField,
  Typography
} from "@mui/material"
import React, { useEffect, useState } from "react"
import { StyledBox, StyledListItem, StyledSubListItem } from "./CompanyDepartmentSelection.styled"
import { useTranslation } from "react-i18next"
import ScrollableListSection from "../ScrollableListSection/ScrollableListSection.tsx"

const DepartmentList = ({ label, count, children }) =>
  count > 5 ? (
    <Box component="li" sx={{ ml: { xs: 1, sm: 4 }, mt: 1, mb: 1.5 }}>
      <ScrollableListSection label={label} count={count}>
        <List disablePadding>{children}</List>
      </ScrollableListSection>
    </Box>
  ) : (
    <>
      <StyledSubListItem sx={{ pl: 3.5 }}>
        <Typography variant="labelMedium" color="textSecondary">
          {label}
        </Typography>
      </StyledSubListItem>
      {children}
    </>
  )

function makeStateFromData(companies, initCompanies, initDepartments, enabled) {
  const companiesWithSelection = []

  if (companies) {
    companies.forEach((c) => {
      const departmentsWithSelection = []
      c.departments.forEach((d) => {
        departmentsWithSelection.push({
          id: d.id,
          name: d.name,
          selected: enabled !== undefined ? enabled : initDepartments?.indexOf(d.id) >= 0
        })
      })
      companiesWithSelection.push({
        id: c.id,
        name: c.name,
        selected: enabled !== undefined ? enabled : initCompanies?.indexOf(c.id) >= 0,
        departments: departmentsWithSelection
      })
    })
  }

  return companiesWithSelection
}
const CompanyDepartmentSelection = ({
  companies,
  initCompanies,
  initDepartments,
  onChange,
  disabled,
  setSelectedCompanyId = undefined,
  mainCompanySelect = false,
  values = null,
  setFieldValue = null,
  handleBlur = null,
  handleChange = null,
  touched = null,
  errors = null
}) => {
  const [state, setState] = useState([])
  const [enabled, setEnabled] = useState(
    companies.every(
      (x) =>
        initCompanies.some((y) => y === x.id) &&
        x.departments.every((d) => initDepartments.some((di) => di === d.id))
    )
  )
  const { t } = useTranslation("translation")

  useEffect(() => {
    const state = makeStateFromData(companies, initCompanies, initDepartments)
    setState(state)
  }, [companies, initCompanies, initDepartments])
  useEffect(() => {
    const allActive = state.every((x) => x.selected && x.departments.every((d) => d.selected))
    setEnabled(allActive)
  }, [state])
  const sortedCompanies = state.sort((a, b) => a?.name.localeCompare(b?.name))
  return (
    <>
      {" "}
      <StyledBox display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">
          {" "}
          {t(`settings.user.company_and_departments`, `Companies and departments`)}
        </Typography>

        <FormControlLabel
          style={{ marginLeft: 2 }}
          label={t(`global.choose_all`, "Choose all")}
          control={
            <Checkbox
              onChange={(e) => {
                const state = makeStateFromData(companies, initCompanies, initDepartments, !enabled)
                setState(state)
                setEnabled(e.target.checked)

                const checked = e.target.checked
                const newCompanies = []

                state.forEach((c) => {
                  const newDepartments = []
                  c.departments.forEach((d) => {
                    newDepartments.push({
                      ...d,
                      selected: checked
                    })
                  })

                  if (c.id === state.id) {
                    newCompanies.push({
                      ...c,
                      selected: checked,
                      departments: newDepartments
                    })
                  } else {
                    newCompanies.push({
                      ...c
                    })
                  }
                })
                setState(newCompanies)
                onChange && onChange(newCompanies)
                const departmentIds = []
                const companyIds = []

                newCompanies.forEach((c) => {
                  if (c.selected) companyIds.push(c.id)
                  c.departments.forEach((d) => {
                    if (d.selected) departmentIds.push(d.id)
                  })
                })
                setFieldValue && setFieldValue("departmentIds", departmentIds)
                setFieldValue && setFieldValue("companyIds", companyIds)
              }}
              id={`company`}
              checked={enabled}
            />
          }
        />
      </StyledBox>
      {mainCompanySelect && state !== undefined && (
        <Box marginY={2}>
          <Autocomplete
            style={{
              width: "100%"
            }}
            componentsProps={{ popper: { style: { width: "240px" } } }}
            id="mainCompanyId"
            options={state !== undefined && state.sort((a, b) => a?.name.localeCompare(b?.name))}
            getOptionLabel={(option) => {
              return option.name ? option.name : ""
            }}
            onChange={(e, value) => {
              const newCompanies = []
              state.forEach((c) => {
                const newDepartments = []
                c.departments.forEach((d) => {
                  newDepartments.push({
                    ...d
                    // selected: true
                  })
                })

                if (c?.id === value?.id) {
                  newCompanies.push({
                    ...c,
                    selected: true,
                    departments: newDepartments
                  })
                } else {
                  newCompanies.push({
                    ...c
                  })
                }
              })
              setState(newCompanies)
              onChange && onChange(newCompanies)
              const departmentIds = []
              const companyIds = []

              newCompanies.forEach((c) => {
                if (c.selected) companyIds.push(c.id)
                c.departments.forEach((d) => {
                  if (d.selected) departmentIds.push(d.id)
                })
              })
              setFieldValue && setFieldValue("departmentIds", departmentIds)
              setFieldValue && setFieldValue("companyIds", companyIds)
              setFieldValue && setFieldValue("mainCompanyId", value?.id)
              setSelectedCompanyId && setSelectedCompanyId(value?.id)
            }}
            value={(state !== undefined && state.find((i) => i.id === values?.mainCompanyId)) || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={t("global.main_company", "Main company")}
                name="mainCompanyId"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.mainCompanyId && errors.mainCompanyId ? true : false}
                helperText={touched.mainCompanyId && errors.mainCompanyId ? errors.mainCompanyId : null}
              />
            )}
          />
        </Box>
      )}
      <List>
        {sortedCompanies.map((company, i) => {
          const sortedDepartments = company.departments.sort((a, b) => a?.name.localeCompare(b?.name))
          return (
            <React.Fragment key={company.hashKey}>
              <StyledListItem>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={disabled}
                      onChange={(e) => {
                        const checked = e.target.checked
                        const newCompanies = []

                        state.forEach((c) => {
                          const newDepartments = []
                          c.departments.forEach((d) => {
                            newDepartments.push({
                              ...d,
                              selected: checked
                            })
                          })

                          if (c.id === company.id) {
                            newCompanies.push({
                              ...c,
                              selected: checked,
                              departments: newDepartments
                            })
                          } else {
                            newCompanies.push({
                              ...c
                            })
                          }
                        })
                        setState(newCompanies)
                        onChange && onChange(newCompanies)
                        const departmentIds = []
                        const companyIds = []

                        newCompanies.forEach((c) => {
                          if (c.selected) companyIds.push(c.id)
                          c.departments.forEach((d) => {
                            if (d.selected) departmentIds.push(d.id)
                          })
                        })
                        setFieldValue && setFieldValue("departmentIds", departmentIds)
                        setFieldValue && setFieldValue("companyIds", companyIds)
                      }}
                      id="company"
                      checked={company.selected}
                    />
                  }
                  label={company.name}
                />
              </StyledListItem>
              {company.departments.length > 0 && (
                <DepartmentList
                  label={t("global.departments", "Departments")}
                  count={company.departments.length}>
                  {sortedDepartments.map((department) => {
                    return (
                      <StyledSubListItem
                        key={department.id}
                        sx={company.departments.length > 5 ? { pl: 1, pr: 1 } : undefined}>
                        <FormControlLabel
                          sx={{
                            ...(company.departments.length > 5
                              ? {
                                  mr: 0,
                                  minWidth: 0,
                                  "& .MuiFormControlLabel-label": {
                                    marginBottom: "2px"
                                  }
                                }
                              : {
                                  "& .MuiFormControlLabel-label": {
                                    marginBottom: "2px"
                                  }
                                })
                          }}
                          control={
                            <Checkbox
                              sx={{
                                padding: 0.5
                              }}
                              disabled={disabled}
                              onChange={(e) => {
                                const checked = e.target.checked

                                const newCompanies = []

                                state.forEach((c) => {
                                  const newDepartments = []
                                  let departmentFound = false
                                  c.departments.forEach((d) => {
                                    if (d.id === department.id) {
                                      departmentFound = true
                                      newDepartments.push({
                                        ...d,
                                        selected: checked
                                      })
                                    } else {
                                      newDepartments.push({
                                        ...d
                                      })
                                    }
                                  })
                                  if (departmentFound) {
                                    newCompanies.push({
                                      ...c,
                                      selected: checked ? checked : c.selected,
                                      departments: newDepartments
                                    })
                                  } else {
                                    newCompanies.push({
                                      ...c
                                    })
                                  }
                                })

                                setState(newCompanies)
                                onChange && onChange(newCompanies)
                                const departmentIds = []
                                const companyIds = []

                                newCompanies.forEach((c) => {
                                  if (c.selected) companyIds.push(c.id)
                                  c.departments.forEach((d) => {
                                    if (d.selected) departmentIds.push(d.id)
                                  })
                                })
                                setFieldValue && setFieldValue("departmentIds", departmentIds)
                                setFieldValue && setFieldValue("companyIds", companyIds)
                              }}
                              id="department"
                              checked={department.selected}
                            />
                          }
                          label={department.name}
                        />
                      </StyledSubListItem>
                    )
                  })}
                </DepartmentList>
              )}
              {sortedCompanies.length > 1 && i !== sortedCompanies.length - 1 && (
                <Box my={1}>
                  <Divider light />
                </Box>
              )}
            </React.Fragment>
          )
        })}
      </List>
    </>
  )
}
export default CompanyDepartmentSelection
